import React, { useState, useEffect } from 'react';
import { DollarSign, TrendingDown, Calendar, Trash2, Plus, Edit, Check, X } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface DebtReductionItem {
  id: string;
  description: string;
  amount: number;
  frequency: 'one-time' | 'monthly' | 'annual';
  date: string;
  category: string;
}

const DebtReduction: React.FC = () => {
  const [items, setItems] = useState<DebtReductionItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAddingItem, setIsAddingItem] = useState(false);
  const [editingItem, setEditingItem] = useState<DebtReductionItem | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);

  useEffect(() => {
    loadItems();
  }, []);

  const loadItems = async () => {
    try {
      const { data, error } = await supabase
        .from('debt_reduction_items')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setItems(data || []);
    } catch (err) {
      console.error('Error loading items:', err);
      setError('Failed to load saved items');
    }
  };

  const [newItem, setNewItem] = useState<Partial<DebtReductionItem>>({
    description: '',
    amount: 0,
    frequency: 'monthly',
    date: new Date().toISOString().split('T')[0],
    category: 'operational'
  });

  const categories = [
    'operational',
    'staffing',
    'marketing',
    'equipment',
    'facilities',
    'other'
  ];

  const frequencies = [
    { value: 'one-time', label: 'One-time' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'annual', label: 'Annual' }
  ];

  const calculateSavings = () => {
    return items.reduce((total, item) => {
      let annualAmount = item.amount;
      switch (item.frequency) {
        case 'monthly':
          annualAmount *= 12;
          break;
        case 'annual':
          break;
        case 'one-time':
          annualAmount = item.amount / 5; // Amortize one-time savings over 5 years
          break;
      }
      return total + annualAmount;
    }, 0);
  };

  const handleAddItem = () => {
    if (!newItem.description || !newItem.amount) return;

    setLoading(true);
    supabase
      .from('debt_reduction_items')
      .insert({
        description: newItem.description,
        amount: newItem.amount,
        frequency: newItem.frequency,
        date: newItem.date,
        category: newItem.category
      })
      .then(async ({ error }) => {
        if (error) throw error;
        await loadItems();
        setNewItem({
          description: '',
          amount: 0,
          frequency: 'monthly',
          date: new Date().toISOString().split('T')[0],
          category: 'operational'
        });
        setIsAddingItem(false);
      })
      .catch(err => {
        console.error('Error adding item:', err);
        setError('Failed to add item');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleSaveEdit = async () => {
    if (!editingItem) return;

    setLoading(true);
    try {
      const { error } = await supabase
        .from('debt_reduction_items')
        .update({
          description: editingItem.description,
          amount: editingItem.amount,
          frequency: editingItem.frequency,
          date: editingItem.date,
          category: editingItem.category
        })
        .eq('id', editingItem.id);

      if (error) throw error;
      await loadItems();
      setEditingItem(null);
    } catch (err) {
      console.error('Error updating item:', err);
      setError('Failed to update item');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    setLoading(true);
    try {
      const { error } = await supabase
        .from('debt_reduction_items')
        .delete()
        .eq('id', id);

      if (error) throw error;
      await loadItems();
      setShowDeleteConfirm(null);
    } catch (err) {
      console.error('Error deleting item:', err);
      setError('Failed to delete item');
    } finally {
      setLoading(false);
    }
  };

  const totalAnnualSavings = calculateSavings();
  const monthlyDebtReduction = totalAnnualSavings / 12;
  const fiveYearProjection = totalAnnualSavings * 5;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">Debt Reduction Planner</h1>
          <p className="text-gray-600">Track and analyze potential cost savings</p>
        </div>
        <button
          onClick={() => setIsAddingItem(true)}
          className="bg-[#ff8200] hover:bg-[#e67600] text-white px-4 py-2 rounded-lg flex items-center"
        >
          <Plus className="h-5 w-5 mr-2" />
          Add Cost Reduction
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center mb-2">
            <DollarSign className="h-6 w-6 text-green-500 mr-2" />
            <h3 className="text-lg font-semibold">Monthly Impact</h3>
          </div>
          <p className="text-3xl font-bold text-green-600">
            ${monthlyDebtReduction.toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </p>
          <p className="text-sm text-gray-500 mt-1">Potential monthly debt reduction</p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center mb-2">
            <Calendar className="h-6 w-6 text-blue-500 mr-2" />
            <h3 className="text-lg font-semibold">Annual Impact</h3>
          </div>
          <p className="text-3xl font-bold text-blue-600">
            ${totalAnnualSavings.toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </p>
          <p className="text-sm text-gray-500 mt-1">Total annual cost reduction</p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center mb-2">
            <TrendingDown className="h-6 w-6 text-purple-500 mr-2" />
            <h3 className="text-lg font-semibold">5-Year Impact</h3>
          </div>
          <p className="text-3xl font-bold text-purple-600">
            ${fiveYearProjection.toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </p>
          <p className="text-sm text-gray-500 mt-1">Projected 5-year savings</p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold mb-4">Cost Reduction Items</h2>

        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Frequency
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {items.map(item => (
                <tr key={item.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingItem?.id === item.id ? (
                      <input
                        type="text"
                        className="border rounded px-2 py-1 w-full"
                        value={editingItem.description}
                        onChange={e => setEditingItem({ ...editingItem, description: e.target.value })}
                      />
                    ) : (
                      item.description
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingItem?.id === item.id ? (
                      <select
                        className="border rounded px-2 py-1"
                        value={editingItem.category}
                        onChange={e => setEditingItem({ ...editingItem, category: e.target.value })}
                      >
                        {categories.map(cat => (
                          <option key={cat} value={cat}>
                            {cat.charAt(0).toUpperCase() + cat.slice(1)}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                        {item.category}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingItem?.id === item.id ? (
                      <input
                        type="number"
                        className="border rounded px-2 py-1 w-24"
                        value={editingItem.amount}
                        onChange={e => setEditingItem({ ...editingItem, amount: Number(e.target.value) })}
                      />
                    ) : (
                      `$${item.amount.toLocaleString()}`
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingItem?.id === item.id ? (
                      <select
                        className="border rounded px-2 py-1"
                        value={editingItem.frequency}
                        onChange={e => setEditingItem({ ...editingItem, frequency: e.target.value as any })}
                      >
                        {frequencies.map(freq => (
                          <option key={freq.value} value={freq.value}>
                            {freq.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      item.frequency
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingItem?.id === item.id ? (
                      <input
                        type="date"
                        className="border rounded px-2 py-1"
                        value={editingItem.date}
                        onChange={e => setEditingItem({ ...editingItem, date: e.target.value })}
                      />
                    ) : (
                      new Date(item.date).toLocaleDateString()
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex space-x-2">
                      {editingItem?.id === item.id ? (
                        <>
                          <button
                            onClick={handleSaveEdit}
                            className="text-green-600 hover:text-green-900"
                          >
                            <Check className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => setEditingItem(null)}
                            className="text-gray-600 hover:text-gray-900"
                          >
                            <X className="h-5 w-5" />
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => setEditingItem(item)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            <Edit className="h-5 w-5" />
                          </button>
                          {showDeleteConfirm === item.id ? (
                            <>
                              <button
                                onClick={() => handleDelete(item.id)}
                                className="text-red-600 hover:text-red-900"
                              >
                                <Check className="h-5 w-5" />
                              </button>
                              <button
                                onClick={() => setShowDeleteConfirm(null)}
                                className="text-gray-600 hover:text-gray-900"
                              >
                                <X className="h-5 w-5" />
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => setShowDeleteConfirm(item.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              <Trash2 className="h-5 w-5" />
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {isAddingItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <h2 className="text-xl font-bold mb-4">Add Cost Reduction Item</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <input
                  type="text"
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#ff8200] focus:border-[#ff8200]"
                  value={newItem.description}
                  onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category
                </label>
                <select
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#ff8200] focus:border-[#ff8200]"
                  value={newItem.category}
                  onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>
                      {cat.charAt(0).toUpperCase() + cat.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Amount
                </label>
                <div className="flex">
                  <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500">
                    $
                  </span>
                  <input
                    type="number"
                    className="flex-1 border border-gray-300 rounded-r-md px-3 py-2 focus:ring-2 focus:ring-[#ff8200] focus:border-[#ff8200]"
                    value={newItem.amount}
                    onChange={(e) => setNewItem({ ...newItem, amount: Number(e.target.value) })}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Frequency
                </label>
                <select
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#ff8200] focus:border-[#ff8200]"
                  value={newItem.frequency}
                  onChange={(e) => setNewItem({ ...newItem, frequency: e.target.value as any })}
                >
                  {frequencies.map(freq => (
                    <option key={freq.value} value={freq.value}>
                      {freq.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date
                </label>
                <input
                  type="date"
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#ff8200] focus:border-[#ff8200]"
                  value={newItem.date}
                  onChange={(e) => setNewItem({ ...newItem, date: e.target.value })}
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setIsAddingItem(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAddItem}
                className="bg-[#ff8200] hover:bg-[#e67600] text-white px-4 py-2 rounded-lg"
              >
                Add Item
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DebtReduction;